import { useRef, useState, useMemo } from 'react';
import _ from 'lodash';

const MAX_STACK_SIZE = 10;

export default () => {
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const setCanDo = () => {
    setCanUndo(undoStackRef.current.length > 0);
    setCanRedo(redoStackRef.current.length > 0);
  };

  const update = useMemo(
    () =>
      _.debounce(
        (value) => {
          redoStackRef.current = [];
          undoStackRef.current.push(value);
          while (undoStackRef.current.length > MAX_STACK_SIZE) {
            undoStackRef.current.shift();
          }
          setCanDo();
        },
        1000,
        // Only push the previous state to the undo stack when the first change happens.
        // If changes continue within a short time, we won't push any more changes
        // until 1s has passed without new changes.
        { leading: true, trailing: false },
      ),
    [],
  );

  // Undo or redo
  const step = (from, to, currentValue) => {
    if (!from.current.length) return null;
    // Reset the debounce timer, so that the next state will get pushed
    // if more changes are made.
    update.cancel();
    to.current.push(currentValue);
    const newValue = from.current.pop();
    setCanDo();
    return newValue;
  };

  const undo = (currentValue) => step(undoStackRef, redoStackRef, currentValue);

  const redo = (currentValue) => step(redoStackRef, undoStackRef, currentValue);

  const reset = () => {
    undoStackRef.current = [];
    redoStackRef.current = [];
    setCanDo();
  };

  return {
    undo,
    redo,
    update,
    reset,
    canUndo,
    canRedo,
  };
};
